'use client';

import { BottomDrawer, Button, Checkbox } from '@common/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';

import { AGREEMENT_TYPE, TERM_CATEGORY } from '@/api';
import { AuthApi, AuthQueryKey, AuthQueryOptions } from '@/api/v2';
import { ROUTES, SEARCH_PARAMS, SELECTOR, STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import {
  termsQueryOptions,
  termsQueryOptionsKeys,
} from '@/query-factory/terms';
import { useGlobalStore } from '@/stores/GlobalStore';

import { LoadingPageTemplate } from './LoadingPageTemplate';

export const PolicyFilterBottomDrawer = () => {
  return (
    <Suspense fallback={<></>}>
      <PolicyFilterBottomDrawerSuspense />
    </Suspense>
  );
};

const PolicyFilterBottomDrawerSuspense = () => {
  const [isPrivateChecked, setIsPrivateChecked] = useState(false);
  const [isSensitiveChecked, setIsSensitiveChecked] = useState(false);
  const searchParams = useSearchParams();

  const policyFilter = searchParams?.get(SEARCH_PARAMS.POLICY_FILTER);

  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  const privateDataConsentQuery = useQuery(
    termsQueryOptions.privateDataConsent(),
  );

  const { data } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member-term-agreement']({
      query: {
        agreementTypeCd: AGREEMENT_TYPE.SENSITIVE_INFO_USE_POLICY_SELECT,
        termCategoryId: TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY,
      },
    }),
  });

  const isPrivateDataConsentChecked = privateDataConsentQuery.data;
  const isSensitiveInfoUsePolicyChecked = data?.context?.agree_yn ?? false;
  const isPolicyFilterOpened =
    !isSensitiveInfoUsePolicyChecked && policyFilter === 'true';

  const privateDataConsentMutation = useMutation({
    mutationFn: (isAgree: boolean) =>
      AuthApi['put:/wello/v2/auth/member-wello/private_data_consent']({
        body: {
          private_data_consent_yn: isAgree,
        },
      }),
    meta: {
      invalidates: [
        [termsQueryOptionsKeys.privateDataConsent],
        AuthQueryKey['get:/wello/v2/auth/member/profile'](),
      ],
    },
  });

  const {
    mutate: sensitiveInfoUsePolicyMutation,
    isPending: isSensitiveInfoUsePolicyPending,
  } = useMutation({
    mutationFn: ({ isChecked }: { isChecked: boolean }) =>
      AuthApi['post:/wello/v2/auth/member-term-agreement']({
        body: {
          agree_yn: isChecked,
          agreement_type_cd: AGREEMENT_TYPE.SENSITIVE_INFO_USE_POLICY_SELECT,
          term_category_id: TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY,
        },
      }),
    meta: {
      invalidates: [
        AuthQueryKey['get:/wello/v2/auth/member/profile'](),
        AuthQueryKey['get:/wello/v2/auth/member-term-agreement'](),
      ],
    },
  });

  const router = useCustomRouter();

  const handleSubmit = () => {
    privateDataConsentMutation.mutate(true);

    sensitiveInfoUsePolicyMutation({
      isChecked: isPrivateDataConsentChecked ? true : isSensitiveChecked,
    });

    const url = new URL(window.location.href);

    url.searchParams.delete(SEARCH_PARAMS.POLICY_FILTER);

    history.replaceState(null, '', url.search);
  };

  const isLoading =
    privateDataConsentMutation.isPending ||
    privateDataConsentQuery.isLoading ||
    isSensitiveInfoUsePolicyPending;

  return privateDataConsentQuery.isFetched ? (
    <BottomDrawer
      opened={isPolicyFilterOpened || !(isPrivateDataConsentChecked ?? true)}
      renderTo={() => document.getElementById(SELECTOR.MAIN_WRAPPER)}
    >
      <BottomDrawer.HandleWrapper>
        <BottomDrawer.Handle />
      </BottomDrawer.HandleWrapper>
      <BottomDrawer.Contents className="flex flex-col pb-16">
        <h2 className="mb-8 font-700 text-Gray-900 typo-body2">
          정책 추천을 받기위해 동의가 필요해요
        </h2>
        <p className="mb-16 text-Gray-700 typo-body4">
          동의하지 않으면 맞춤정보를 추천받을 수 없어요.
        </p>
        {isPrivateDataConsentChecked ? null : (
          <p className="text-Gray-black typo-body4">
            <Checkbox
              checked={isPrivateDataConsentChecked || isPrivateChecked}
              onCheckedChange={() => {
                setIsPrivateChecked(!isPrivateChecked);
              }}
            >
              <span className="font-500">(필수)</span> 개인정보 수집 및 이용
              동의
              <button
                className="ml-4 font-600 underline"
                type="button"
                onClick={() =>
                  setTermsDrawerType(
                    TERM_CATEGORY.FIT_PRIVACY_USE_POLICY_SELECT,
                  )
                }
              >
                보기
              </button>
            </Checkbox>
          </p>
        )}
        <p className="mb-28 text-Gray-black typo-body4">
          <Checkbox
            checked={isSensitiveChecked || isSensitiveInfoUsePolicyChecked}
            onCheckedChange={() => {
              setIsSensitiveChecked(!isSensitiveChecked);
            }}
          >
            <span className="font-500">(선택)</span> 민감정보 수집 및 이용 동의
            <button
              className="ml-4 font-600 underline"
              type="button"
              onClick={() =>
                setTermsDrawerType(TERM_CATEGORY.SENSITIVE_INFO_USE_POLICY)
              }
            >
              보기
            </button>
          </Checkbox>
        </p>
        <Button
          disabled={!(isPrivateChecked || isPrivateDataConsentChecked)}
          loading={isLoading}
          onClick={handleSubmit}
        >
          동의하고 넘어가기
        </Button>
        <Button
          loading={isLoading}
          theme="sub-text"
          onClick={async () => {
            if (isPrivateDataConsentChecked) {
              const url = new URL(window.location.href);

              url.searchParams.delete(SEARCH_PARAMS.POLICY_FILTER);

              history.replaceState(null, '', url.search);

              return;
            }

            await privateDataConsentMutation.mutateAsync(false);

            const policyFilterLandingFrom = sessionStorage.getItem(
              STORAGE_KEY.POLICY_FILTER_LANDING_FROM,
            );

            sessionStorage.removeItem(STORAGE_KEY.POLICY_FILTER_LANDING_FROM);

            router.push(policyFilterLandingFrom || ROUTES.HOME.pathname);
          }}
        >
          동의하지 않기
        </Button>
      </BottomDrawer.Contents>
    </BottomDrawer>
  ) : (
    <LoadingPageTemplate />
  );
};
